import moment from 'moment/moment';
import PropTypes from 'prop-types';
import React from 'react';
import { Trans } from 'react-i18next';

import { Button } from '@shared/ui/components/Button';
import { Icon } from '@shared/ui/components/Icon';
import { cn } from '@shared/utils/helpers';

import { ajaxErrorHandler } from '../../../libs/utils';
import {
  createRegistrationCateringOption,
  getRegistrationAvailableCateringOptions,
  getRegistrationCateringOptions,
} from '../../../services/api/registrations';
import HotTable from '../../vendor/handsontable/react-handsontable.jsx';

class CateringTable extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      colHeaders: [],
      rowHeaders: [],
      columns: [],
      data: [],
      limits: [],
      types: [],
      done: false,
      isFullscreen: false,
    };

    this.amountOfMaxRenderer = this.amountOfMaxRenderer.bind(this);
    this.handleCellChange = this.handleCellChange.bind(this);
    this._disabledCellCheck = this._disabledCellCheck.bind(this);
    this.toggleFullscreen = this.toggleFullscreen.bind(this);

    if (props.admin) {
      this.cateringAmountValidator = this.adminCateringAmountValidator;
    } else {
      this.cateringAmountValidator = this.companyCateringAmountValidator;
    }
  }

  static propTypes = {
    registration: PropTypes.object,
    admin: PropTypes.bool,
  };

  async componentDidMount() {
    let colHeaders = [];
    let tempLimits = [];
    let tempData = [];
    let rowHeaders = [];
    let columns = [];
    let days = [];
    let types = [];

    const availableCateringOptions = await getRegistrationAvailableCateringOptions(this.props.registration.id, {
      include: 'catering',
    });

    const registrationCateringOptions = await getRegistrationCateringOptions(this.props.registration.id);

    availableCateringOptions.data.forEach((type) => {
      colHeaders.push(type.name);
      types.push(type.id);

      columns.push({
        data: 'cateringOption_' + type.id,
        type: 'numeric',
        renderer: this.amountOfMaxRenderer,
        validator: this.cateringAmountValidator,
        allowInvalid: false,
      });

      type.limitPerDay.forEach((day) => {
        if (!tempLimits[day.day]) {
          const dayObj = {
            day: moment(day.day, 'YYYY-MM-DD').format('ddd DD/MM'),
          };

          tempLimits[day.day] = { ...dayObj };
          tempData[day.day] = { ...dayObj };
          days.push(day.day);
        }

        tempLimits[day.day]['cateringOption_' + type.id] = parseInt(day.limit, 10);
        tempData[day.day]['cateringOption_' + type.id] = 0;
      });
    });

    // Add days from registrationCateringOptions if they are not in availableCateringOptions
    registrationCateringOptions.data.forEach((r) => {
      if (!tempLimits[r.day]) {
        const dayObj = {
          day: moment(r.day, 'YYYY-MM-DD').format('ddd DD/MM'),
        };

        tempLimits[r.day] = { ...dayObj };
        tempData[r.day] = { ...dayObj };
        days.push(r.day);
      }
    });

    let limits = [];
    let data = [];

    Object.keys(tempLimits).forEach(function (key) {
      rowHeaders.push(tempLimits[key].day);
      limits.push(tempLimits[key]);
      data.push(tempData[key]);
    });

    this.setState({
      colHeaders,
      rowHeaders,
      limits,
      columns,
      data,
      days,
      types,
      done: true,
    });

    window.cateringLimits = limits;

    registrationCateringOptions.data.forEach((r) => {
      if (data[days.indexOf(r.day)]) {
        data[days.indexOf(r.day)]['cateringOption_' + r.cateringOptionId] = r.quantity;
      }
    });

    this.setState({ data });
  }

  amountOfMaxRenderer(instance, td, row, col, prop, value, cellProperties) {
    if (this.state.limits[row][prop] > 0 || this.props.admin) {
      let max = this.state.limits[row][prop] ?? 0;
      let displayValue = value ?? 0;

      td.innerHTML = `${displayValue} <span class="tableMax">of max ${max}</span>`;
    } else {
      td.innerHTML = '';
    }

    return td;
  }

  companyCateringAmountValidator(query, callback) {
    if (/^\d+$/.test(query) && parseInt(query, 10) <= window.cateringLimits[this.row][this.prop]) {
      callback(true);
    } else {
      callback(false);
    }
  }

  adminCateringAmountValidator(query, callback) {
    if (/^\d+$/.test(query)) {
      callback(true);
    } else {
      callback(false);
    }
  }

  _disabledCellCheck(row, col, prop) {
    var cellProperties = {};

    if (this.state.limits[row] && this.state.limits[row][prop] === 0 && !this.props.admin) {
      cellProperties.readOnly = true;
    }

    return cellProperties;
  }

  // changes: 2D array containing information about each of the edited cells [[row, prop, oldVal, newVal], ...].
  handleCellChange(changes) {
    if (changes) {
      changes.forEach((change) => {
        const data = {
          cateringOptionId: change[1].split('_')[1],
          quantity: change[3],
          day: this.state.days[change[0]],
        };
        createRegistrationCateringOption(this.props.registration.id, data)
          .then((response) => {
            const cateringOptions = [].concat(this.state.cateringOptions);
            const oldCateringOption = cateringOptions.filter(
              (option) => option.cateringOptionId === response.cateringOptionId && option.day === response.day
            )[0];

            if (!oldCateringOption) {
              cateringOptions.push(response);
            }

            this.setState({
              cateringOptions,
            });
          })
          .catch(ajaxErrorHandler);
      });
    }
  }

  toggleFullscreen() {
    this.setState((prevState) => ({
      isFullscreen: !prevState.isFullscreen,
    }));

    if (!this.state.isFullscreen) {
      document.getElementById('root').classList.add('overflow-hidden');
    } else {
      document.getElementById('root').classList.remove('overflow-hidden');
    }
  }

  render() {
    const { colHeaders, data, done, rowHeaders, columns, isFullscreen } = this.state;

    return (
      <div>
        {done && data.length > 0 && (
          <div>
            <div className={`cateringTable ${isFullscreen ? 'fullscreen' : ''}`}>
              <Button
                className={cn('ml-auto w-full sm:w-auto mb-4', {
                  'my-2 ml-2': isFullscreen,
                })}
                variant="outline"
                onClick={this.toggleFullscreen}
              >
                {isFullscreen ? <Icon icon="cross" /> : <Icon icon="fullscreen" />}
                {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen Table'}
              </Button>
              <HotTable
                root="hot"
                data={data}
                colHeaders={colHeaders}
                rowHeaders={rowHeaders}
                columns={columns}
                preventOverflow="horizontal"
                afterChange={this.handleCellChange}
                cells={this._disabledCellCheck}
                stretchH="all"
                rowHeaderWidth={100}
                height={(35 + data.length * 32 + 5)}
                fillHandle={{
                  autoInsertRow: false,
                }}
              />
            </div>
          </div>
        )}

        {done && data.length === 0 && (
          <div className="alert alert-warning">
            <Trans>CateringRequestNoCrewMembersMsg</Trans>
          </div>
        )}
      </div>
    );
  }
}

export default CateringTable;
